import { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronLeft, ChevronRight, FileDownIcon } from "lucide-react";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { BiBasket } from "react-icons/bi";
import { Link, useParams } from "react-router-dom";
import { LANG } from "@/types";
import useStore from "@/hooks/use-store";
import { useCart } from "@/contexts/CartContext";
import { BsFillBasketFill } from "react-icons/bs";
import useItems from "@/hooks/use-item";
import { TbTargetArrow } from "react-icons/tb";
import { Tooltip } from "@mui/material";
import StoreLayout from "@/layouts/storeLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  useTheme,
  Fade,
  Rating,
  Avatar,
  Tabs as MuiTabs,
  Tab,
  useMediaQuery,
} from "@mui/material";

import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Store,
} from "@mui/icons-material";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Person, ShoppingCart, MyLocation } from "@mui/icons-material";
import { cn } from "@/lib/utils";
import LanguageSelector from "@/components/language-selector";
import { BiHeart } from "react-icons/bi";

// const mockProduct = {
//   name: "Professional Digital Stethoscope",
//   price: 299.99,
//   minPrice: 399.99,
//   rating: 4.5,
//   reviewCount: 128,
//   quantity: 50,
//   description: {
//     en: "High-quality digital stethoscope with advanced acoustic technology. Features include:\n• Enhanced sound amplification\n• Noise reduction technology\n• Bluetooth connectivity\n• Rechargeable battery\n• 2-year warranty",
//     ar: "سماعة طبية رقمية احترافية مع تقنية صوتية متقدمة. الميزات تشمل:\n• تضخيم صوت محسن\n• تقنية تقليل الضوضاء\n• اتصال بلوتوث\n• بطارية قابلة لإعادة الشحن\n• ضمان لمدة عامين",
//   },
//   specifications: [
//     { key: "Brand", value: "MedTech Pro" },
//     { key: "Model", value: "DST-2000" },
//     { key: "Weight", value: "180g" },
//     { key: "Battery Life", value: "8 hours" },
//     { key: "Warranty", value: "2 years" },
//   ],
//   images: [
//     "https://res.cloudinary.com/sddderert/image/upload/v1745778745/680d272aa78ad8086969616f/images/9a5358b9-436d-4200-8ee0-6c6c4b08392f.jpeg.jpg",
//     "https://res.cloudinary.com/sddderert/image/upload/v1745778745/680d272aa78ad8086969616f/images/9a5358b9-436d-4200-8ee0-6c6c4b08392f.jpeg.jpg",
//     "https://res.cloudinary.com/sddderert/image/upload/v1745778745/680d272aa78ad8086969616f/images/9a5358b9-436d-4200-8ee0-6c6c4b08392f.jpeg.jpg",
//   ],
//   store: {
//     name: "MedTech Supplies",
//     rating: 4.8,
//     reviewCount: 256,
//     location: "Riyadh, Saudi Arabia",
//     phone: "+966 12 345 6789",
//     email: "contact@medtechsupplies.com",
//     description:
//       "Your trusted source for medical equipment and supplies since 2010. We specialize in high-quality medical devices and professional healthcare solutions.",
//   },
//   reviews: [
//     {
//       id: 1,
//       user: "Dr. Ahmed",
//       rating: 5,
//       date: "2024-03-15",
//       comment:
//         "Excellent quality and sound clarity. The Bluetooth feature is very useful for recording patient sounds.",
//     },
//     {
//       id: 2,
//       user: "Nurse Sarah",
//       rating: 4,
//       date: "2024-03-10",
//       comment:
//         "Great stethoscope, but the battery life could be better. Otherwise, very satisfied with the purchase.",
//     },
//   ],
// };

// Custom arrow components for the slider
const NextArrow = ({ onClick }: { onClick?: () => void }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: "absolute",
      right: 16,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 10,
      bgcolor: "rgba(0,0,0,0.5)",
      color: "white",
      "&:hover": {
        bgcolor: "rgba(0,0,0,0.7)",
      },
    }}
  >
    <ChevronRight />
  </IconButton>
);

const PrevArrow = ({ onClick }: { onClick?: () => void }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: "absolute",
      left: 16,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 10,
      bgcolor: "rgba(0,0,0,0.5)",
      color: "white",
      "&:hover": {
        bgcolor: "rgba(0,0,0,0.7)",
      },
    }}
  >
    <ChevronLeft />
  </IconButton>
);

interface Product {
  categories: string[];
  companyId: string;
  id: string;
  descAr: string;
  descEn: string;
  pdfLink: string;
  brand: string;
  countryOfOrigin: string;
  sfdaMdma: string;
  name: string;
  price: number;
  descriptionAr: string;
  leadTime: string;
  descriptionEn: string;
  quantity: number;
  images: string[];
  rating: number;
  reviewCount: number;
  minOrder: number;
  unit: string;
  store: {
    id: string;
    name: string;
  };
}

type Store = {
  name: string;
  rating: number;
  reviewCount: number;
  location: string;
  email: string;
  imgUrl: string;
  bioAr: string;
  bioEn: string;
};

function ProductPage() {
  const { locale } = useLocale();
  const { id } = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [productInfo, setProductInfo] = useState<Product>();
  const [storeInfo, setStoreInfo] = useState<Store>();
  const { getPublishedItem } = useItems();
  const { getStoreById } = useStore();
  const {
    addToCart,
    removeFromCart,
    cartItems,
    updateQuantity,
    addToInquiry,
    removeFromInquiry,
    isInInquiry,
    totalItems,
    inquiryItems,
  } = useCart();
  const [quantity, setQuantity] = useState(productInfo?.minOrder || 1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [favorites, setFavorites] = useState<string[]>(() => {
    const savedFavorites = localStorage.getItem("favorites");
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });
  const isFavorite = productInfo && favorites.includes(productInfo.id);

  const isInCart = cartItems.some((item) => item.id === id);
  const cartItem = cartItems.find((item) => item.id === id);
  const inInquiry = isInInquiry(id!);

  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else if (productInfo) {
      setQuantity(productInfo.minOrder);
    }
  }, [cartItem, productInfo]);

  const handleQuantityChange = (newQuantity: number) => {
    if (productInfo && newQuantity >= productInfo.minOrder) {
      setQuantity(newQuantity);
      if (isInCart) {
        updateQuantity(id!, newQuantity);
      }
    }
  };

  const handleCartToggle = () => {
    if (isInCart) {
      removeFromCart(id!);
      setQuantity(productInfo?.minOrder || 1);
    } else {
      if (productInfo) {
        addToCart({
          ...productInfo,
          quantity,
          categories: productInfo.categories,
          companyId: productInfo.companyId,
        });
      }
    }
  };

  const handleInquiryToggle = () => {
    if (inInquiry) {
      removeFromInquiry(id!);
    } else {
      if (productInfo) {
        const productWithQuantity = {
          ...productInfo,
          quantity: quantity,
        };
        addToInquiry(productWithQuantity);
      }
    }
  };

  const toggleFavorite = () => {
    if (!productInfo) return;
    setFavorites((prev) => {
      const newFavorites = prev.includes(productInfo.id)
        ? prev.filter((id) => id !== productInfo.id)
        : [...prev, productInfo.id];
      localStorage.setItem("favorites", JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (id) {
        const product = await getPublishedItem(id);
        setProductInfo(product);
        if (product.store.id) {
          const store = await getStoreById(product.store.id);
          setStoreInfo(store);
        }
      }
    };
    fetchProduct();
  }, [id]);

  const { t } = useTranslate(locale);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dotsClass: "slick-dots !bottom-2",
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <div>
      {isDesktop ? (
        <StoreLayout>
          <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto", py: 3 }}>
            <Grid container spacing={4}>
              {/* Product Images */}
              <Grid item xs={12} md={6}>
                <Fade in={true}>
                  <Box sx={{ position: "relative" }}>
                    <Card
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ position: "relative", aspectRatio: "1/1" }}>
                        {productInfo?.images &&
                        productInfo.images.length > 1 ? (
                          <Slider {...sliderSettings}>
                            {productInfo.images.map((image, index) => (
                              <Box key={index} sx={{ aspectRatio: "1/1" }}>
                                <img
                                  src={image}
                                  alt={`${productInfo?.name} - Image ${
                                    index + 1
                                  }`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            ))}
                          </Slider>
                        ) : (
                          <Box sx={{ aspectRatio: "1/1" }}>
                            <img
                              src={productInfo?.images?.[0]}
                              alt={productInfo?.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        )}
                        <Button
                          className="!absolute top-3 right-3 h-10 w-10 rounded-full z-10"
                          onClick={toggleFavorite}
                          sx={{ minWidth: 0, p: 0 }}
                        >
                          <BiHeart
                            className={`w-6 h-6 transition-colors duration-200 ${
                              isFavorite
                                ? "text-red-500 fill-red-500"
                                : "text-gray-500"
                            }`}
                          />
                        </Button>
                      </Box>
                    </Card>
                  </Box>
                </Fade>
              </Grid>

              {/* Product Details */}
              <Grid item xs={12} md={6}>
                <Fade in={true}>
                  <Card
                    elevation={2}
                    sx={{
                      boxShadow: "none",
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      height: "fit-content",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h4" fontWeight={600} gutterBottom>
                        {productInfo?.name}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 3,
                        }}
                      >
                        <Rating
                          value={productInfo?.rating || 0}
                          precision={0.5}
                          readOnly
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {productInfo?.rating} ({productInfo?.reviewCount}{" "}
                          reviews)
                        </Typography>
                      </Box>

                      {/* Price */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h5"
                          fontWeight={600}
                          color="#18181b"
                        >
                          ${productInfo?.price}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("checkout.fields.quantity")} (Min:{" "}
                          {productInfo?.minOrder || 1})
                        </Typography>
                      </Box>

                      {/* Quantity Controls */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <IconButton
                          aria-label="Decrease quantity"
                          onClick={() => handleQuantityChange(quantity - 1)}
                          disabled={
                            quantity <= (productInfo?.minOrder || 1) || isInCart
                          }
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor:
                              quantity <= (productInfo?.minOrder || 1) ||
                              isInCart
                                ? "grey.100"
                                : "#f4f4f5",
                            color:
                              quantity <= (productInfo?.minOrder || 1) ||
                              isInCart
                                ? "grey.400"
                                : "#18181b",
                            border: "1.2px solid #e5e7eb",
                            fontSize: 20,
                            transition: "all 0.2s",
                            "&:active": { bgcolor: "#e5e7eb" },
                          }}
                        >
                          <RemoveIcon fontSize="inherit" />
                        </IconButton>
                        <Typography
                          variant="h6"
                          sx={{
                            minWidth: 32,
                            textAlign: "center",
                            fontWeight: 700,
                            color: "#18181b",
                            fontSize: 18,
                          }}
                        >
                          {quantity}
                        </Typography>
                        <IconButton
                          aria-label="Increase quantity"
                          onClick={() => handleQuantityChange(quantity + 1)}
                          disabled={isInCart}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: isInCart ? "grey.100" : "#f4f4f5",
                            color: isInCart ? "grey.400" : "#18181b",
                            border: "1.2px solid #e5e7eb",
                            fontSize: 20,
                            transition: "all 0.2s",
                            "&:active": { bgcolor: "#e5e7eb" },
                          }}
                        >
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                        {/* Total Price */}
                        <Typography
                          variant="body1"
                          sx={{
                            ml: 2,
                            fontWeight: 700,
                            color: "#18181b",
                            fontSize: 16,
                          }}
                        >
                          ${((productInfo?.price || 0) * quantity).toFixed(2)}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                        <Tooltip
                          title={
                            inInquiry ? "Remove from Inquiry" : "Add to Inquiry"
                          }
                        >
                          <Button
                            className={cn(
                              "!text-zinc-900 !border-1 !border-zinc-900",
                              inInquiry && "!bg-zinc-950 !text-white"
                            )}
                            startIcon={<TbTargetArrow />}
                            onClick={handleInquiryToggle}
                            sx={{ borderRadius: 2, flex: 1 }}
                          >
                            {inInquiry ? "In Inquiry" : "Inquiry"}
                          </Button>
                        </Tooltip>

                        <Button
                          className="!bg-zinc-900 !text-white"
                          startIcon={
                            isInCart ? <BsFillBasketFill /> : <BiBasket />
                          }
                          onClick={handleCartToggle}
                          sx={{ borderRadius: 2, flex: 1 }}
                        >
                          {isInCart ? "In Cart" : "Add to Cart"}
                        </Button>
                      </Box>

                      {/* Product Tabs */}
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <MuiTabs
                          value={selectedTab}
                          onChange={handleTabChange}
                          variant="fullWidth"
                          sx={{
                            "& .MuiTabs-indicator": {
                              background: "#18181b",
                            },
                            "& .MuiTab-root": {
                              textTransform: "none",
                              fontWeight: 600,
                            },
                          }}
                        >
                          <Tab
                            label={t("buyer_pages.return_request.description")}
                          />
                          <Tab
                            label={t(
                              "buyer_pages.return_request.specifications"
                            )}
                          />
                          <Tab
                            label={t("buyer_pages.return_request.reviews")}
                          />
                        </MuiTabs>
                      </Box>

                      {/* Tab Content */}
                      <Box sx={{ mt: 3, minHeight: 200 }}>
                        {selectedTab === 0 && (
                          <Fade in={true}>
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ whiteSpace: "pre-line" }}
                              >
                                {productInfo && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        locale == LANG.AR
                                          ? productInfo?.descriptionAr ||
                                            productInfo?.descAr
                                          : productInfo?.descriptionEn ||
                                            productInfo?.descEn,
                                    }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </Fade>
                        )}

                        {selectedTab === 1 && (
                          <Fade in={true}>
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  py: 1.5,
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("item.fields.brand")}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {productInfo?.brand}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  py: 1.5,
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("item.fields.countryOfOrigin")}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {productInfo?.countryOfOrigin}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  py: 1.5,
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("item.fields.leadTime")}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {productInfo?.leadTime || "-"}
                                </Typography>
                              </Box>
                              {productInfo?.pdfLink ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 1.5,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t("item.fields.catalogue")}
                                  </Typography>
                                  <Link
                                    to={productInfo?.pdfLink}
                                    target="_blank"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                                    rel="noopener noreferrer"
                                  >
                                    <FileDownIcon className="w-4 h-4" />
                                    {t("actions.download")}
                                  </Link>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 1.5,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t("item.fields.unit")}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {productInfo?.unit || "-"}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Fade>
                        )}

                        {selectedTab === 2 && (
                          <Fade in={true}>
                            <Box>
                              {/* {mockProduct.reviews.map((review) => (
                                <Box
                                  key={review.id}
                                  sx={{
                                    py: 2,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                    >
                                      {review.user}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {review.date}
                                    </Typography>
                                  </Box>
                                  <Rating
                                    value={review.rating}
                                    readOnly
                                    size="small"
                                    sx={{ mb: 1 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {review.comment}
                                  </Typography>
                                </Box>
                              ))} */}
                            </Box>
                          </Fade>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            </Grid>

            {/* Store Information */}
            <Box sx={{ mt: 6 }}>
              <Fade in={true}>
                <Card
                  elevation={2}
                  sx={{
                    boxShadow: "none",
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Store color="primary" sx={{ fontSize: 32 }} />
                      <Typography variant="h5" fontWeight={600}>
                        {t("marketplace.storeInformation")}
                      </Typography>
                    </Box>

                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                          }}
                        >
                          <Avatar
                            src={storeInfo?.imgUrl}
                            alt={storeInfo?.name}
                            sx={{ width: 64, height: 64 }}
                          />
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {storeInfo?.name}
                            </Typography>
                            {/* <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Rating
                                value={mockProduct.store.rating}
                                readOnly
                                size="small"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ({mockProduct.store.reviewCount} reviews)
                              </Typography>
                            </Box> */}
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3 }}
                        >
                          {locale == LANG.AR
                            ? storeInfo?.bioAr
                            : storeInfo?.bioEn}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ space: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            {/* <LocationOn color="action" /> */}
                            {/* <Typography variant="body2">
                              {mockProduct.store.location}
                            </Typography> */}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            {/* <Phone color="action" /> */}
                            {/* <Typography variant="body2">
                              {mockProduct.store.phone}
                            </Typography> */}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {/* <Email color="action" /> */}
                            {/* <Typography variant="body2">
                              {mockProduct.store.email}
                            </Typography> */}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Fade>
            </Box>
          </Box>
        </StoreLayout>
      ) : (
        <Box
          sx={{
            width: "100vw",
            maxWidth: "100vw",
            p: 0,
            m: 0,
            bgcolor: "background.default",
            position: "relative",
          }}
        >
          {/* Product Image - full width, no card, no border, no shadow */}
          <Box sx={{ position: "relative" }}>
            {productInfo?.images && productInfo.images.length > 1 ? (
              <Slider {...sliderSettings}>
                {productInfo.images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{ width: "100vw", aspectRatio: "1/1", m: 0, p: 0 }}
                  >
                    <img
                      src={image}
                      alt={`${productInfo?.name} - Image ${index + 1}`}
                      style={{
                        width: "100vw",
                        height: "100vw",
                        objectFit: "cover",
                        display: "block",
                        margin: 0,
                        padding: 0,
                      }}
                    />
                  </Box>
                ))}
              </Slider>
            ) : (
              <Box sx={{ width: "100vw", aspectRatio: "1/1", m: 0, p: 0 }}>
                <img
                  src={productInfo?.images?.[0]}
                  alt={productInfo?.name}
                  style={{
                    width: "100vw",
                    height: "100vw",
                    objectFit: "cover",
                    display: "block",
                    margin: 0,
                    padding: 0,
                  }}
                />
              </Box>
            )}
            {/* Heart button - mobile */}
            <Button
              // variant="ghost"
              // size="icon"
              className="!absolute top-3 right-3 h-10 w-10 rounded-full z-10"
              onClick={toggleFavorite}
              sx={{
                minWidth: 0,
                p: 0,
                position: "absolute",
                top: 12,
                right: 12,
              }}
            >
              <BiHeart
                className={`w-6 h-6 transition-colors duration-200 ${
                  isFavorite ? "text-red-500 fill-red-500" : "text-gray-500"
                }`}
              />
            </Button>
          </Box>
          {/* Product Info & Quantity Controls - mobile style */}
          <Box sx={{ p: 2, pb: 8 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              gutterBottom
              sx={{ fontSize: "1.2rem" }}
            >
              {productInfo?.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Rating
                value={productInfo?.rating || 0}
                precision={0.5}
                readOnly
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {productInfo?.rating} ({productInfo?.reviewCount} 5 reviews)
              </Typography>
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color="#18181b"
              sx={{ mb: 0.5, fontSize: "1.3rem" }}
            >
              ${productInfo?.price}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("checkout.fields.quantity")} (Min: {productInfo?.minOrder || 1}
              )
            </Typography> */}
            {/* Quantity Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <IconButton
                aria-label="Decrease quantity"
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= (productInfo?.minOrder || 1) || isInCart}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor:
                    quantity <= (productInfo?.minOrder || 1) || isInCart
                      ? "grey.100"
                      : "#f4f4f5",
                  color:
                    quantity <= (productInfo?.minOrder || 1) || isInCart
                      ? "grey.400"
                      : "#18181b",
                  border: "1.2px solid #e5e7eb",
                  fontSize: 20,
                  transition: "all 0.2s",
                  "&:active": { bgcolor: "#e5e7eb" },
                }}
              >
                <RemoveIcon fontSize="inherit" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  minWidth: 32,
                  textAlign: "center",
                  fontWeight: 700,
                  color: "#18181b",
                  fontSize: 18,
                }}
              >
                {quantity}
              </Typography>
              <IconButton
                aria-label="Increase quantity"
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={isInCart}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: isInCart ? "grey.100" : "#f4f4f5",
                  color: isInCart ? "grey.400" : "#18181b",
                  border: "1.2px solid #e5e7eb",
                  fontSize: 20,
                  transition: "all 0.2s",
                  "&:active": { bgcolor: "#e5e7eb" },
                }}
              >
                <AddIcon fontSize="inherit" />
              </IconButton>
              {/* Total Price */}
              <Typography
                variant="body1"
                sx={{ ml: 2, fontWeight: 700, color: "#18181b", fontSize: 16 }}
              >
                ${((productInfo?.price || 0) * quantity).toFixed(2)}
              </Typography>
            </Box>
            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Button
                className={cn(
                  "!text-zinc-900 !border-1 !border-zinc-900",
                  inInquiry && "!bg-zinc-950 !text-white"
                )}
                startIcon={<TbTargetArrow />}
                onClick={handleInquiryToggle}
                sx={{ borderRadius: 2, flex: 1, fontWeight: 700 }}
              >
                {inInquiry ? "In Inquiry" : "Inquiry"}
              </Button>
              <Button
                className="!bg-zinc-900 !text-white"
                startIcon={isInCart ? <BsFillBasketFill /> : <BiBasket />}
                onClick={handleCartToggle}
                sx={{ borderRadius: 2, flex: 1, fontWeight: 700 }}
              >
                {isInCart ? "In Cart" : "Add to Cart"}
              </Button>
            </Box>
            {/* Tabs and Tab Content (optional: can be simplified for mobile) */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <MuiTabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label={t("buyer_pages.return_request.description")} />
                <Tab label={t("buyer_pages.return_request.specifications")} />
                <Tab label={t("buyer_pages.return_request.reviews")} />
              </MuiTabs>
            </Box>
            <Box sx={{ mt: 1, minHeight: 120 }}>
              {selectedTab === 0 && (
                <Fade in={true}>
                  <Box>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                      {productInfo && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              locale == LANG.AR
                                ? productInfo?.descriptionAr ||
                                  productInfo?.descAr
                                : productInfo?.descriptionEn ||
                                  productInfo?.descEn,
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Fade>
              )}
              {selectedTab === 1 && (
                <Fade in={true}>
                  <Box>
                    {/* You can simplify or style the specs for mobile here */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("item.fields.brand")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productInfo?.brand}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("item.fields.countryOfOrigin")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productInfo?.countryOfOrigin}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("item.fields.leadTime")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productInfo?.leadTime || "-"}
                      </Typography>
                    </Box>
                    {productInfo?.pdfLink ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("item.fields.catalogue")}
                        </Typography>
                        <Link
                          to={productInfo?.pdfLink}
                          target="_blank"
                          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                          rel="noopener noreferrer"
                        >
                          <FileDownIcon className="w-4 h-4" />
                          {t("actions.download")}
                        </Link>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("item.fields.unit")}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {productInfo?.unit || "-"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Fade>
              )}
              {selectedTab === 2 && (
                <Fade in={true}>
                  <Box>
                    {/* {mockProduct.reviews.map((review) => (
                      <Box
                        key={review.id}
                        sx={{
                          py: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            {review.user}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {review.date}
                          </Typography>
                        </Box>
                        <Rating
                          value={review.rating}
                          readOnly
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {review.comment}
                        </Typography>
                      </Box>
                    ))} */}
                  </Box>
                </Fade>
              )}
            </Box>
          </Box>
          {/* Spacer for bottom nav */}
          <Box sx={{ height: 64 }} />
          {/* Mobile Bottom Navigation */}
          <MobileBottomNav
            items={[
              {
                icon: <Person sx={{ fontSize: 24 }} />,
                label: t("profile.account"),
                to: "/login",
              },
              {
                icon: <MyLocation sx={{ fontSize: 24 }} />,
                label: "RFQ",
                to: "/inquiry-form",
                badge:
                  inquiryItems.length > 0 ? inquiryItems.length : undefined,
              },
              {
                icon: <ShoppingCart sx={{ fontSize: 24 }} />,
                label: t("buyer_pages.cart.title"),
                to: "/cart",
                badge: totalItems > 0 ? totalItems : undefined,
              },
              {
                icon: <LanguageSelector className="!flex !flex-col" />,
                label: "",
                to: "#",
              },
            ]}
          />
        </Box>
      )}
    </div>
  );
}

export default ProductPage;
